@if (!zones.length) {
  <div class="d-flex flex-column align-items-center justify-content-center common-empty-container">
    <p class="common-empty-header">No zones yet</p>
    <p class="common-empty-sub-text">Create a zone first; each zone gets its own floor plan.</p>
  </div>
} @else {
  <section class="fp">
    <header class="fp-head">
      <div class="fp-zones" role="tablist" aria-label="Zones">
        @for (zone of zones; track zoneId(zone)) {
          <button
            type="button"
            role="tab"
            class="fp-zone-tab"
            [class.fp-zone-tab--active]="zoneId(zone) === selectedZoneId"
            [attr.aria-selected]="zoneId(zone) === selectedZoneId"
            (click)="selectZone(zone)"
          >
            {{ zone.zone_name }}
            <span class="fp-zone-count">{{ zoneTableCount(zone) }}</span>
          </button>
        }
      </div>

      <div class="fp-grid-size" aria-label="Grid size">
        <span class="fp-label">Grid</span>
        <input
          type="number"
          class="fp-num"
          [min]="minGrid"
          [max]="maxGrid"
          [(ngModel)]="gridDraft.columns"
          aria-label="Columns"
        />
        <span class="fp-x">×</span>
        <input
          type="number"
          class="fp-num"
          [min]="minGrid"
          [max]="maxGrid"
          [(ngModel)]="gridDraft.rows"
          aria-label="Rows"
        />
        <button
          type="button"
          class="fp-btn"
          matTooltip="Save the grid size on this zone"
          [disabled]="gridDraft.columns == grid.columns && gridDraft.rows == grid.rows"
          (click)="applyGrid()"
        >
          Apply
        </button>
      </div>

      <div class="fp-actions">
        @if (shapeMode) {
          <span class="fp-dirty">{{ outlineDraft.length }} points</span>
          <button
            type="button"
            class="fp-btn"
            [disabled]="!outlineDraft.length"
            matTooltip="Remove the last point"
            (click)="undoVertex()"
          >
            <i class="bx bx-undo" aria-hidden="true"></i> Undo
          </button>
          @if (hasSavedOutline) {
            <button
              type="button"
              class="fp-btn fp-btn--danger"
              matTooltip="Remove the shape so the zone is the whole grid"
              (click)="resetShape()"
            >
              Reset shape
            </button>
          }
          <button type="button" class="fp-btn" (click)="cancelShapeEdit()">Cancel</button>
          <button type="button" class="fp-btn fp-btn--primary" (click)="finishShape()">
            <i class="bx bx-check" aria-hidden="true"></i> Done
          </button>
        } @else {
          <button
            type="button"
            class="fp-btn"
            matTooltip="Trace the room outline on the grid"
            (click)="startShapeEdit()"
          >
            <i class="bx bx-vector" aria-hidden="true"></i>
            {{ hasSavedOutline ? 'Edit shape' : 'Draw shape' }}
          </button>
          @if (dirtyCount) {
            <span class="fp-dirty">{{ dirtyCount }} unsaved</span>
            <button type="button" class="fp-btn" (click)="discard()">Discard</button>
          }
          <button
            type="button"
            class="fp-btn fp-btn--primary"
            [disabled]="!dirtyCount || saving"
            (click)="save()"
          >
            <i class="bx bx-save" aria-hidden="true"></i> Save layout
          </button>
        }
      </div>
    </header>

    <div class="fp-body">
      <div class="fp-canvas-wrap" #canvasWrap>
        <div
          #canvas
          class="fp-canvas"
          [class.fp-canvas--shape]="shapeMode"
          [style.width.px]="canvasWidth"
          [style.height.px]="canvasHeight"
          [style.backgroundSize]="cellPx + 'px ' + cellPx + 'px'"
          (click)="onCanvasClick($event)"
        >
          <!-- Zone outline: outside hatched, inside tinted, vertices editable. -->
          <svg
            class="fp-outline"
            [class.fp-outline--editing]="shapeMode"
            [attr.width]="canvasWidth"
            [attr.height]="canvasHeight"
            aria-hidden="true"
          >
            <defs>
              <pattern id="fp-hatch" width="8" height="8" patternUnits="userSpaceOnUse">
                <path d="M -2 2 L 2 -2 M 0 8 L 8 0 M 6 10 L 10 6" class="fp-hatch-line" />
              </pattern>
            </defs>
            @if (outline && outline.length >= 3) {
              <path class="fp-outline-outside" [attr.d]="outsideMaskPath" fill-rule="evenodd" />
              <polygon class="fp-outline-shape" [attr.points]="outlinePoints" />
            } @else if (shapeMode && outline && outline.length > 1) {
              <polyline class="fp-outline-open" [attr.points]="outlinePoints" />
            }
            @if (shapeMode) {
              @for (point of outlineDraft; track $index) {
                <circle
                  class="fp-vertex"
                  [class.fp-vertex--first]="$index === 0"
                  [attr.cx]="point.x * cellPx"
                  [attr.cy]="point.y * cellPx"
                  r="6"
                  (mousedown)="startVertexDrag($event, $index)"
                  (touchstart)="startVertexDrag($event, $index)"
                  (dblclick)="removeVertex($index, $event)"
                  (contextmenu)="removeVertex($index, $event)"
                />
              }
            }
          </svg>

          @for (item of placed; track item.table.table_id) {
            <div
              class="fp-table"
              cdkDrag
              cdkDragBoundary=".fp-canvas"
              [cdkDragDisabled]="shapeMode"
              [cdkDragFreeDragPosition]="item.pos"
              (cdkDragStarted)="onDragStarted()"
              (cdkDragEnded)="onDragEnded($event, item)"
              [class.fp-table--selected]="item.table.table_id === selectedTableId"
              [class.fp-table--outside]="item.outside"
              [class.fp-table--frozen]="shapeMode"
              [style.width.px]="item.rect.width * cellPx"
              [style.height.px]="item.rect.height * cellPx"
              (click)="selectTable(item.table, $event)"
            >
              <div class="fp-table-inner" [style.transform]="'rotate(' + item.rotation + 'deg)'">
                <app-table-glyph
                  [name]="item.table.table_name"
                  [seats]="item.table.number_of_seats"
                  [shape]="item.table.shape"
                  [status]="item.table.status"
                  [width]="item.rect.width * cellPx"
                  [height]="item.rect.height * cellPx"
                  [selected]="item.table.table_id === selectedTableId"
                  [dimmed]="isDimmed(item.table)"
                />
              </div>
              @if (item.table.table_id === selectedTableId) {
                <button
                  type="button"
                  class="fp-resize"
                  aria-label="Resize table"
                  matTooltip="Drag to resize"
                  (mousedown)="startResize($event, item)"
                  (touchstart)="startResize($event, item)"
                ></button>
              }
            </div>
          }

          @if (!placed.length && !shapeMode) {
            <div class="fp-canvas-empty">
              <i class="bx bx-layout" aria-hidden="true"></i>
              <p>No tables placed in this zone yet.</p>
              <p class="fp-canvas-hint">
                Use <strong>Place</strong> on a table to the right, then drag it into position.
              </p>
            </div>
          }
        </div>
      </div>

      <aside class="fp-side">
        @if (shapeMode) {
          <div class="fp-panel">
            <div class="fp-panel-head">
              <h3 class="fp-panel-title">Zone shape</h3>
              <span class="fp-panel-sub">{{ selectedZone?.zone_name }}</span>
            </div>
            <p class="fp-hint">
              Click grid corners to trace the room. Drag a point to move it, double-click or
              right-click a point to remove it. Press <strong>Done</strong> (or Enter) to save,
              Escape to cancel.
            </p>
            <p class="fp-hint">
              Tables outside the shape are marked but still saved, so you can trace first and tidy
              up after.
            </p>
          </div>
        } @else if (outsideCount) {
          <div class="fp-warning" role="status">
            <i class="bx bx-error" aria-hidden="true"></i>
            {{ outsideCount }} {{ outsideCount === 1 ? 'table is' : 'tables are' }} outside the zone
            shape
          </div>
        }
        @if (selectedPlaced; as sel) {
          <div class="fp-panel">
            <div class="fp-panel-head">
              <h3 class="fp-panel-title">{{ sel.table.table_name }}</h3>
              <span class="fp-panel-sub"
                >{{ sel.table.number_of_seats }} seats · {{ sel.table.shape | titlecase }}</span
              >
            </div>

            <div class="fp-field">
              <span class="fp-label">Size (cells)</span>
              <div class="fp-field-row">
                <input
                  type="number"
                  class="fp-num"
                  min="1"
                  [max]="grid.columns"
                  [ngModel]="sel.rect.width"
                  (ngModelChange)="setSize('width', $event)"
                  aria-label="Width in cells"
                />
                <span class="fp-x">×</span>
                <input
                  type="number"
                  class="fp-num"
                  min="1"
                  [max]="grid.rows"
                  [ngModel]="sel.rect.height"
                  (ngModelChange)="setSize('height', $event)"
                  aria-label="Height in cells"
                />
              </div>
            </div>

            <div class="fp-field">
              <span class="fp-label">Rotation</span>
              <div class="fp-field-row">
                <button
                  type="button"
                  class="fp-icon-btn"
                  matTooltip="Rotate left"
                  (click)="rotate(-rotateStep)"
                >
                  <i class="bx bx-rotate-left" aria-hidden="true"></i>
                </button>
                <span class="fp-rotation">{{ sel.rotation }}°</span>
                <button
                  type="button"
                  class="fp-icon-btn"
                  matTooltip="Rotate right"
                  (click)="rotate(rotateStep)"
                >
                  <i class="bx bx-rotate-right" aria-hidden="true"></i>
                </button>
                <button
                  type="button"
                  class="fp-link"
                  [disabled]="sel.rotation === 0"
                  (click)="resetRotation()"
                >
                  Reset
                </button>
              </div>
            </div>

            <div class="fp-field">
              <span class="fp-label">Position</span>
              <span class="fp-value">col {{ sel.rect.x + 1 }}, row {{ sel.rect.y + 1 }}</span>
            </div>

            <button type="button" class="fp-btn fp-btn--danger" (click)="unplace(sel)">
              <i class="bx bx-eraser" aria-hidden="true"></i> Remove from plan
            </button>

            <p class="fp-hint">Arrow keys move · Shift for 5 cells · R rotates · Delete removes</p>
          </div>
        } @else {
          <div class="fp-panel fp-panel--muted">
            <p class="fp-hint">Select a table on the plan to resize, rotate or remove it.</p>
          </div>
        }

        <div class="fp-tray">
          <div class="fp-tray-head">
            <h3 class="fp-panel-title">Unplaced</h3>
            <span class="fp-zone-count">{{ unplaced.length }}</span>
          </div>
          @if (!unplaced.length) {
            <p class="fp-hint">Every table in this zone is on the plan.</p>
          }
          @for (table of unplaced; track table.table_id) {
            <div class="fp-tray-item" [class.fp-tray-item--dimmed]="isDimmed(table)">
              <app-table-glyph
                [name]="table.table_name"
                [seats]="table.number_of_seats"
                [shape]="table.shape"
                [status]="table.status"
                [width]="64"
                [height]="48"
              />
              <div class="fp-tray-meta">
                <span class="fp-tray-name">{{ table.table_name }}</span>
                <span class="fp-tray-sub"
                  >{{ table.number_of_seats }} seats · {{ table.shape | titlecase }}</span
                >
              </div>
              <button type="button" class="fp-btn fp-btn--small" (click)="place(table)">
                Place
              </button>
            </div>
          }
        </div>
      </aside>
    </div>
  </section>
}
